import { useMemo } from "react";
import { ServerInferResponseBody } from "@ts-rest/core";
import { VStack } from "@design-system/VStack/VStack";
import { categoryContract } from "@common/contracts/category.contract";
import { Category } from "@common/types/category";
import { TaxonomyMainCategories } from "@common/categories";
import { pluralize } from "@common/helpers/string";
import { useCategoriesTree } from "@hooks/useCategoriesTree";
import { Segment } from "@helpers/segment";
import { SegmentBar } from "@design-system/SegmentBar/SegmentBar";
import styles from "./CategoryStats.module.scss";
import { statusGradientColors } from "./CategoryStatColors";
interface CategoryEntry {
    id: string;
}

interface Props {
    statsMap: Map<
        string,
        ServerInferResponseBody<
            typeof categoryContract.getCategoryStatistics
        >[number]
    >;
    stats: ServerInferResponseBody<
        typeof categoryContract.getCategoryStatistics
    >;
    total: number;
    categories: CategoryEntry[];
    toggleCategory: (categoryId: string) => void;
}

const CategoryStatTooltip = ({
    category,
    valueOverride,
    subtitle,
}: {
    category: Category;
    valueOverride: string;
    subtitle: React.ReactNode;
}) => (
    <VStack className={styles.categoryTooltip}>
        <span className={styles.headline}>{category.label}</span>
        {!category.parentCategoryId && (
            <span className={styles.subHeadline}>(Main Category)</span>
        )}
        <span className={styles.value}>{valueOverride}</span>
        <span className={styles.subValue}>{subtitle}</span>
    </VStack>
);

const getValueOverride = (
    statsMap: Map<
        string,
        ServerInferResponseBody<
            typeof categoryContract.getCategoryStatistics
        >[number]
    >,
    category: Category,
    total: number,
): string =>
    `${(((statsMap.get(category.id)?.count ?? 0) / total) * 100).toFixed(2)}%`;

const getSubtitle = (
    statsMap: Map<
        string,
        ServerInferResponseBody<
            typeof categoryContract.getCategoryStatistics
        >[number]
    >,
    category: Category,
): string =>
    `${statsMap.get(category.id)?.count ?? 0} ${pluralize("transaction", statsMap.get(category.id)?.count ?? 0)}`;

export const CategoriesStats: React.FC<Props> = ({
    statsMap,
    total,
    categories,
    toggleCategory,
    stats,
}) => {
    const categoriesTree = useCategoriesTree();

    const segments = useMemo(() => {
        if (!total) {
            return [];
        }

        return stats
            .filter((stat) => !stat.parentCategoryId)
            .map((stat) => {
                const category = categoriesTree.find(
                    ({ id }) => id === stat.id,
                );

                if (!category) {
                    return;
                }

                const isHidden = !categories.some(
                    ({ id }) => id === category.id,
                );

                const segment: Segment & {
                    isHidden?: boolean;
                } = {
                    id: category.id,
                    label: category.label,
                    value: isHidden
                        ? 0
                        : (statsMap.get(category.id)?.count ?? 0),
                    valueOverride: getValueOverride(statsMap, category, total),
                    subtitle: getSubtitle(statsMap, category),
                    colors: statusGradientColors[
                        category.taxonomy as TaxonomyMainCategories
                    ],
                    isHidden,
                    subsegments: category.subcategories
                        .filter(
                            (subcategory) =>
                                categories.some(
                                    // eslint-disable-next-line sonarjs/no-nested-functions
                                    ({ id }) => id === subcategory.id,
                                ) &&
                                (statsMap.get(subcategory.id)?.count ?? 0) > 0,
                        )
                        .map((subcategory) => ({
                            id: subcategory.id,
                            label: category.label,
                            value: statsMap.get(subcategory.id)?.count ?? 0,
                            valueOverride: getValueOverride(
                                statsMap,
                                category,
                                total,
                            ),
                            subtitle: getSubtitle(statsMap, category),
                            tooltip: (
                                <CategoryStatTooltip
                                    category={subcategory}
                                    valueOverride={getValueOverride(
                                        statsMap,
                                        subcategory,
                                        total,
                                    )}
                                    subtitle={getSubtitle(
                                        statsMap,
                                        subcategory,
                                    )}
                                />
                            ),
                        })),
                };

                segment.tooltip = (
                    <CategoryStatTooltip
                        category={category}
                        valueOverride={segment.valueOverride ?? ""}
                        subtitle={segment.subtitle}
                    />
                );

                // If there are subsegments, we want to override the main segments display info
                // with the overall stats for the main category
                if (segment.subsegments && segment.subsegments.length > 0) {
                    if (segment.value > 0) {
                        // adding the main category as the first subsegment
                        segment.subsegments.unshift({
                            ...segment,
                        });
                        // the segment as first subsegment's subsegments must be set to undefined, otherwise,
                        // the subsegment widths are incorrectly calcualted
                        (segment.subsegments[0] as Segment).subsegments =
                            undefined;
                    }
                    const segmentTotalWithSubsegments =
                        segment.subsegments.reduce(
                            (acc, subsegment) => acc + subsegment.value,
                            0,
                        );
                    segment.value = isHidden ? 0 : segmentTotalWithSubsegments;
                    segment.subtitle = `${segmentTotalWithSubsegments} ${pluralize("transaction", segmentTotalWithSubsegments)}`;
                    segment.valueOverride = `${((segmentTotalWithSubsegments / total) * 100).toFixed(2)}%`;
                }
                return segment;
            })
            .filter((segment) => segment !== undefined);
    }, [total, categoriesTree, categories, statsMap, stats]);

    return (
        <div className={styles.categoryStatsContainer}>
            <SegmentBar
                className={styles.categoryStats}
                segments={segments}
                hasMinimumWidth
                showSubsegmentsOnHover
                onSegmentClick={toggleCategory}
            />
        </div>
    );
};
